import { RefObject, MouseEvent, useState } from 'react';
import { TFunction } from 'i18next';
import { ActionViews, Views, computeState, focusHeadlineFromRefAndOutlineBlock } from './BlockInteractions.utils';
import { Icons, SVGIcon } from '../../atoms/SVG';
import { BtnModifier } from '../../molecules/Btn';
import { BlockType } from '../../../types/api/ElementType';
import { Namespace } from '../../../translation/namespace';
import { getIndex, getPosition } from '../../blocks/ContentWrapper/ContentWrapper.utils';
import { ButtonKeys, ButtonsType, HeadlineType, ViewsType } from './BlockInteractionsTypes';
import { ButtonType } from '../../../types/ButtonType';
import { ContentWrapperContext } from '../../blocks/ContentWrapper/ContentWrapperContext';
import { RefreshCallback } from '../../../_lib/FetchContainer';

export function useBlockInteractions(
  headingRef: RefObject<HTMLElement | null>,
  views: ViewsType,
  headline: HeadlineType,
  id: string,
  elementType: string,
  uuid: string,
  t: TFunction,
  buttons?: ButtonsType,
  contentWrapperProps?: ContentWrapperContext | null,
  refreshBlock?: RefreshCallback,
  alwaysShowInfo?: boolean,
  activeView?: string,
) {
  const [state, setState] = useState(computeState(activeView));

  /** sets either primary or seconday view */
  const toggleActiveView = (event: MouseEvent, newViewName: string): void => {
    event.preventDefault();
    setState(prevState => computeState(newViewName, prevState));
  };

  const showDefaultView = (): void => {
    setState(prevState => computeState(Views.DEFAULT, prevState));
    focusHeadlineFromRefAndOutlineBlock(headingRef);
  };

  /**
   * Returns all views parsed via views property _or_ content-wrapper actions
   * that we handle as views
   *
   * @returns View[] an array of Views
   */
  const getAllViews = () => {
    return Object.keys(views).concat(
      Object.keys(contentWrapperProps?.actions || []).filter(actionName => ActionViews.includes(actionName as any)),
    );
  };

  const viewExists = (viewName: any): boolean => {
    return getAllViews().includes(viewName);
  };

  /**
   * A Modal gets opened only by secondary Views
   * we want to get it's buttonRef and focus it.
   */
  const closeSecondaryView = (): void => {
    setState({ ...state, secondaryView: undefined });
  };

  /** we don't want to change the href on state change, a11y reason. */
  const getHref = (viewName: string): undefined | string => {
    const { primaryView, secondaryView } = state;

    if (viewName && ['acceptActivation', 'rejectActivation', 'action'].includes(viewName)) {
      return undefined;
    }

    // return to default href if we can close the button
    if ([primaryView, secondaryView].includes(viewName as any)) {
      const href = views?.default?.href;
      return href ? `${href}#${id}` : `#${id}`;
    }

    // change to href if we click the button
    if (viewName && views[viewName]) {
      const { href } = views[viewName] as any;
      return href ? `${href}#${id}` : `#${id}`;
    }

    return `#${id}`;
  };

  const getButtonModifier = (svgSpriteId: SVGIcon): [BtnModifier] | [BtnModifier, BtnModifier] => {
    if (svgSpriteId === Icons.ACCEPT_CHECK) {
      return [BtnModifier.SINGLE_ACTION_YES];
    }
    if (svgSpriteId === Icons.DECLINE_CROSS) {
      return [BtnModifier.SINGLE_ACTION_NO];
    }
    return [BtnModifier.MINOR_ACTION, BtnModifier.TRANSPARENT];
  };

  /**
   * Some blocks of ContentWrapper have position in button text and some not, therefore we have to
   * differentiate by prefix withoutPosition and normal blocks other than ContentWrapper have no position.
   * */
  const getTitleText = (
    contentWrapperPropsPar: any,
    transNamespaceAndKey: string,
    elementTypePar: string,
    textType: string,
  ): string => {
    if (contentWrapperPropsPar?.position) {
      return t(`${transNamespaceAndKey}.${textType}`, contentWrapperPropsPar?.position) as string;
    }
    if (
      !contentWrapperPropsPar?.position &&
      (elementTypePar === BlockType.richtext || elementTypePar === BlockType.video)
    ) {
      return t(`${transNamespaceAndKey}.${textType}WithoutPosition`);
    }
    return t(`${transNamespaceAndKey}.${textType}`);
  };

  const getModalProps = (): {
    headline: string;
    initialFocusIndex: number;
  } => {
    const { secondaryView } = state;

    if (contentWrapperProps && secondaryView && ActionViews.includes(secondaryView)) {
      return {
        headline: t(
          `${Namespace.ContentWrapper}:actions.${secondaryView}.modalTitle.${elementType}`,
          // Here we have to call getPosition, because contentWrapper does not know which actionType is clicked
          // @ts-expect-error TODO: define which getPosition function is suitable
          getPosition(contentWrapperProps.index, undefined, elementType, false, secondaryView),
        ),
        initialFocusIndex: 1,
      };
    }
    if (buttons && secondaryView && buttons[secondaryView]) {
      return {
        headline: getTitleText(
          contentWrapperProps,
          buttons[secondaryView as keyof Record<ButtonKeys, ButtonType>].transNamespaceAndKey,
          elementType,
          'text',
        ),
        initialFocusIndex: buttons[secondaryView as keyof Record<ButtonKeys, ButtonType>].initialFocusIndex || 1,
      };
    }

    // This case didn't exist. Added to avoid typescript warning.
    return {
      headline: '',
      initialFocusIndex: 1,
    };
  };

  /** Returns properties for the secondaryView */
  const getSecondaryViewProps = (): any => {
    const { secondaryView } = state;

    if (secondaryView && ActionViews.includes(secondaryView)) {
      // deleteblock & connectblocks
      return {
        transnamespaceAndKey: `${Namespace.ContentWrapper}:actions.${secondaryView}.${elementType}`,
        headline:
          secondaryView !== 'connectBlocks'
            ? t(`${Namespace.ContentWrapper}:actions.${secondaryView}.confirmationText.${elementType}`)
            : null,
        form: contentWrapperProps?.actions[secondaryView as 'deleteBlock' | 'connectBlocks'],
        refreshBlock: () => {
          contentWrapperProps?.refetch(uuid);
          setState({ ...state, secondaryView: undefined });
        },
      };
    }
    if (buttons && views && secondaryView) {
      return {
        transnamespaceAndKey: buttons[secondaryView as keyof Record<ButtonKeys, ButtonType>].transNamespaceAndKey,
        headline: t(`${buttons[secondaryView as keyof Record<ButtonKeys, ButtonType>].transNamespaceAndKey}.question`),
        refreshBlock,
        // @ts-expect-error TODO
        ...views[secondaryView].embedded,
      };
    }
  };

  /**
   * We always show the info and the buttons, if we have more than the default view.
   * we show the info according to the props, if there is only one view, e.g the default.
   * @return {bool}
   */
  const showInfoWithNoButtons = (): boolean | undefined => {
    return !!getAllViews().filter(key => key !== Views.DEFAULT).length || alwaysShowInfo;
  };

  /**
   * Returns text of headline(for movable richtext and contentimage blocks with position)
   * @return {string}
   */
  const getHeadlinePosition = (): string | null => {
    if (contentWrapperProps && Object.keys(contentWrapperProps).length) {
      const positionText = t(`${Namespace.ContentWrapper}:position`);
      return `(${positionText} ${getIndex(
        contentWrapperProps?.index,
        contentWrapperProps?.baseIndex,
        false,
      )})`;
    }

    return null;
  };

  return {
    state,
    setState,
    toggleActiveView,
    showDefaultView,
    viewExists,
    closeSecondaryView,
    getHref,
    getButtonModifier,
    getTitleText,
    getModalProps,
    getSecondaryViewProps,
    showInfoWithNoButtons,
    getHeadlinePosition,
  };
}
